/*
 * The reducer takes care of state changes in our app through actions
 */

import invariant from 'invariant';
import {
  CHANGE_FORM,
  SET_AUTH,
  SET_PERMISSIONS,
  SENDING_REQUEST,
  REQUEST_ERROR,
  CLEAR_ERROR,
  SHOW_PROGRESS_BAR,
  HIDE_PROGRESS_BAR,
  SHOW_LOADING_SPINNER,
  CHANGE_FORM_BUSINESS,
  CHANGE_BUSINESS_TYPES_DATA,
  SET_AUTH_FROM_LOCAL,
  CHANGE_BUSINESS_OPENINGS,
  CHANGE_BUSINESS_CATEGORIES_DATA,
  CHANGE_BUSINESS_CATEGORIES_ALL_DATA,
  CHANGE_BUSINESS_STARTS_DATA,
  CHANGE_BUSINESS_LOCATIONS_DATA,
  CHANGE_BUSINESS_PROVINCE_DATA,
  CHANGE_BUSINESS_CITY_DATA,
  CHANGE_BUSINESS_ZIPCODE_DATA,
  CHANGE_BUSINESS_KECAMATAN_DATA,
  CHANGE_BANK_LIST_DATA,
  CHANGE_BUSINESS_SALES_DATA,
  CHANGE_FORM_REGISTER,
  REDIRECT_TO,
  LOAD_RESET_PASSWORD,
  CHANGE_FORM_RESET_PASSWORD,
  SET_PREPARE_ENCRYPTION,
  SET_PROFILE,
  CLEAR_REGISTER_FORM,
  SET_IS_REGISTERED,
  SET_RESEND_VERIFICATION_EMAIL_STATE,
  DASHBOARD_SHOW_MODAL_OUTLET_MOBILE,
  SET_AUTH_FROM_API,
  SET_EXTERNAL_REDIRECT_URL,
  SHOW_ACTIVE_APPLICATIONS,
  SET_BUSINESS_INFO_DATA,
} from 'actions/constants';
import { SET_MODAL_COMPLETE_BUSINESS_PROFILE } from 'actions/business/constants';
import { SAVE_UPLOAD_IMAGE } from 'actions/uploadImage/constants';
import {
  RESET_CITY_DROPDOWN,
  RESET_ZIPCODE_DROPDOWN,
  RESET_KECAMATAN_DROPDOWN,
} from 'actions/kyb/constants';
import { loggedIn } from 'api/auth';
import { isFSA } from 'flux-standard-action';

const store = require('store');

// The initial application state
const initialState = {
  bankListState: [],
  businessOpeningChoices: [],
  businessCategoriesAllState: [],
  activeApplicationsState: [],
  businessCategoriesState: [],
  businessCityState: {},
  businessKecamatanState: {},
  businessLocationsState: [],
  businessProvinceState: {},
  businessSalesState: [],
  businessStartsState: [],
  businessState: {
    business_name: '',
    business_type: '',
    business_category: '',
    business_address: '',
    business_city: '',
    business_province: '',
    business_postal_code: '',
    user_first_name: '',
    user_last_name: '',
    user_phone: '',
  },
  businessTypesState: [],
  businessZipcodeState: [],
  currentlySending: false,
  currentOutlet: [],
  encryption: {
    public_modulus: '',
    public_exponent: '',
    key: '',
  },
  error: {}, // error object - based on FSA (Flux Standard Action), this must be Error() object
  externalRedirectURL: '',
  forgotState: {
    email: '',
  },
  formRegState: {
    email: '',
    password: '',
    confpassword: '',
  },
  formState: {
    email: '',
    password: '',
  },
  hasError: false, // determine if the currentState hasError
  isRegistered: false,
  loadingBar: 0,
  loggedIn: loggedIn(),
  outlets: [],
  permissions: [],
  resendVerificationEmail: {
    isModalShow: false,
    headerMessage: 'Error',
    bodyMessage: '',
    modalStatus: '',
  },
  resetPasswordState: {
    token_error: true,
    message: '',
    userFullName: '',
  },
  showLoadingSpinner: false,
  showModalOutletMobile: false,
  showWelcomeModal: null,
  // object of authenticated user. null if not authenticated
  user: null,
  businessInfo: null,
};

// Takes care of changing the application state
function reducer(state = initialState, action) {
  invariant(
    isFSA(action),
    `Invalid FSA action. See https://github.com/acdlite/flux-standard-action for info. Object: ${JSON.stringify(
      action
    )}`
  );

  const { payload = {}, error = null } = action;

  // check if action has key 'error' then hasError must be changed to true
  let hasError = false;
  if (error) {
    hasError = true;
  }

  switch (action.type) {
    case CHANGE_FORM:
      return { ...state, hasError, formState: payload.newFormState };
    case SET_AUTH:
      // FIXME side effects
      payload.user && store.set('response:login', payload.user);
      return {
        ...state,
        hasError,
        loggedIn: payload.newAuthState,
        user: payload.user,
      };
    case CHANGE_FORM_REGISTER:
      return { ...state, hasError, formRegState: payload.newFormRegState };
    case CHANGE_FORM_BUSINESS:
      return { ...state, hasError, businessState: payload.newBusinessState };
    case SENDING_REQUEST:
      return { ...state, hasError, currentlySending: payload.sending };
    case REQUEST_ERROR:
      return { ...state, hasError, error: payload.error };
    case CLEAR_ERROR:
      return { ...state, hasError: false, error };
    case SHOW_PROGRESS_BAR:
      return { ...state, hasError, loadingBar: state.loadingBar + 30 };
    case SHOW_LOADING_SPINNER:
      return {
        ...state,
        hasError,
        showLoadingSpinner: payload.showLoadingSpinner,
      };
    case HIDE_PROGRESS_BAR:
      return {
        ...state,
        hasError,
        loadingBar: state.loadingBar > 0 ? state.loadingBar - 30 : 0,
      };
    case CHANGE_BUSINESS_TYPES_DATA:
      return {
        ...state,
        hasError,
        businessTypesState: payload.newBusinessTypes,
      };
    case CHANGE_BUSINESS_CATEGORIES_DATA:
      return {
        ...state,
        hasError,
        businessCategoriesState: payload.newBusinessCategories,
      };
    case CHANGE_BUSINESS_CATEGORIES_ALL_DATA:
      return {
        ...state,
        hasError,
        businessCategoriesAllState: payload.newBusinessCategoriesAll,
      };
    case CHANGE_BUSINESS_OPENINGS:
      return {
        ...state,
        hasError,
        businessOpeningChoices: payload.newBusinessOpeningChoices,
      };
    case SHOW_ACTIVE_APPLICATIONS:
      return {
        ...state,
        hasError,
        activeApplicationsState: payload.showActiveApplications,
      };
    case CHANGE_BUSINESS_STARTS_DATA:
      return {
        ...state,
        hasError,
        businessStartsState: payload.newBusinessStarts,
      };
    case CHANGE_BUSINESS_LOCATIONS_DATA:
      return {
        ...state,
        hasError,
        businessLocationsState: payload.newBusinessLocations,
      };
    case CHANGE_BUSINESS_PROVINCE_DATA:
      return {
        ...state,
        hasError,
        businessProvinceState: payload.newBusinessProvince,
      };
    case CHANGE_BUSINESS_CITY_DATA:
      return { ...state, hasError, businessCityState: payload.newBusinessCity };
    case RESET_CITY_DROPDOWN:
      return { ...state, businessCityState: {} };
    case CHANGE_BUSINESS_ZIPCODE_DATA:
      return {
        ...state,
        hasError,
        businessZipcodeState: payload.newBusinessZipcode,
      };
    case RESET_ZIPCODE_DROPDOWN:
      return { ...state, businessZipcodeState: [] };
    case CHANGE_BUSINESS_KECAMATAN_DATA:
      return {
        ...state,
        hasError,
        businessKecamatanState: payload.newBusinessKecamatan,
      };
    case RESET_KECAMATAN_DROPDOWN:
      return { ...state, businessKecamatanState: {} };
    case CHANGE_BANK_LIST_DATA:
      return { ...state, hasError, bankListState: payload.newBankList };
    case CHANGE_BUSINESS_SALES_DATA:
      return {
        ...state,
        hasError,
        businessSalesState: payload.newBusinessSales,
      };
    case SET_AUTH_FROM_LOCAL:
      const loginResponse = store.get('response:login');
      let user = null;
      if (loginResponse) {
        user = loginResponse;
      }
      return { ...state, hasError, loggedIn: !!user, user };
    case REDIRECT_TO:
      return { ...state, hasError, url: payload.url };
    case LOAD_RESET_PASSWORD:
      return { ...state, hasError, url: payload.token };
    case CHANGE_FORM_RESET_PASSWORD:
      return {
        ...state,
        hasError,
        resetPasswordState: payload.resetPasswordState,
      };
    case SET_PREPARE_ENCRYPTION:
      return { ...state, hasError, encryption: payload.encryption };
    case SET_PERMISSIONS:
      return { ...state, permissions: payload.permissions };
    case SET_PROFILE:
      return { ...state, user: payload.profile };
    case SET_BUSINESS_INFO_DATA:
      return { ...state, businessInfo: payload.newBusinessInformationData };
    case CLEAR_REGISTER_FORM:
      return {
        ...state,
        formRegState: { email: '', password: '', confpassword: '' },
      };
    case SET_IS_REGISTERED:
      return { ...state, isRegistered: payload.isRegistered };
    case SET_RESEND_VERIFICATION_EMAIL_STATE:
      return {
        ...state,
        resendVerificationEmail: payload.resendVerificationEmail,
      };
    case DASHBOARD_SHOW_MODAL_OUTLET_MOBILE:
      return { ...state, showModalOutletMobile: payload.showModalOutletMobile };
    case SAVE_UPLOAD_IMAGE:
      return { ...state, user: { ...state.user, gravatar: payload.filename } };
    case SET_AUTH_FROM_API:
      const profileResponse = payload.data;
      let profile = null;
      if (profileResponse) {
        profile = profileResponse;
      }
      return { ...state, hasError, loggedIn: !!profile, user: profile };
    case SET_EXTERNAL_REDIRECT_URL:
      return { ...state, externalRedirectURL: payload.url };
    case SET_MODAL_COMPLETE_BUSINESS_PROFILE:
      return { ...state, showWelcomeModal: payload.showWelcomeModal };
    default:
      return { ...state, hasError: false };
  }
}

export default reducer;
